import type { FC } from "react";
import React, { useEffect, useMemo } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { BLOOD_SPOT_SKU } from "src/constants/misc";
import Path from "src/constants/path";
import PushNotificationsBloc from "src/hybrid/components/PushNotificationsControls";
import { createAppPath, preserveQueryParams } from "src/lib/createAppPath";
import SignupCubit from "src/state/SignupCubit/SignupCubit";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { useBloc } from "src/state/state";
import AnimatedPage from "src/ui/components/AnimatedPage/AnimatedPage";
import AppPageProvider from "src/ui/components/AppPageProvider/AppPageProvider";
import AnnouncementCardDialog from "src/ui/components/AppQueryPopups/dialogs/AnnouncementCardDialog";
import AppStatusBar from "src/ui/components/AppStatusBar/AppStatusBar";
import CustomSuspense from "src/ui/components/CustomSuspense/CustomSuspense";
import LabResultsDataSubPageRouter from "src/ui/components/LabResultsDataSubPageRouter/LabResultsDataSubPageRouter";
import { PlanOverview } from "src/ui/components/PlanOverview/PlanOverview";
import ProfileOverview from "src/ui/components/ProfileOverview/ProfileOverview";
import ProfileRouter from "src/ui/components/ProfileOverview/ProfileRouter";
import ProgramDetails from "src/ui/components/ProgramDetails/ProgramDetails";
import ProgramOverview from "src/ui/components/ProgramOverview/ProgramOverview";
import ReadMore from "src/ui/components/ReadMore/ReadMore";
import { Prompts } from "src/ui/pages/AppPage/components/Prompts/Prompts";
import HomePage from "src/ui/pages/HomePage/HomePage";
import AppQueryPopupsRedirects from "../AppQueryPopups/AppQueryPopupsRedirects";
import { ChatBloc } from "../Chat/ChatBloc";
import InAppQuestionnaire from "../InAppQuestionnaire/InAppQuestionnaire";
import PlanRouter from "../PlanRouter/PlanRouter";
import MealInsightOverview from "../MealInsights/MealInsightOverview";
import { useBlocNext } from "@blac/react";
import RedirectWithQueryParams from "../RedirectWithQueryParams/RedirectWithQueryParams";
import { FeatureFlags } from "src/lib/featureFlags";
import LeapChatRouter from "../LeapChat/LeapChatRouter";
import LegacyChatView from "../LeapChat/LegacyChatView";

const Async = {
  CustomerIo: React.lazy(
    async () => import("src/ui/components/CustomerIo/CustomerIo")
  )
};

export const DisableAppPadding = () => (
  <div className="disable-app-padding" style={{ display: "none" }} />
);

const AppRouter: FC = () => {
  const [userPreferences, { loadUserPreferences }] =
    useBlocNext(UserPreferencesCubit);
  const [, { isConversationsEnabled }] = useBloc(FeatureFlags);

  const navigate = useNavigate();
  const location = useLocation();

  const [, { setNavigateMethod }] = useBloc(PushNotificationsBloc, {
    subscribe: false
  });
  const [, { registerWebsocketObserversForNotification, checkUnreadMessages }] =
    useBlocNext(ChatBloc);

  setNavigateMethod(navigate);

  const onboardingDone = useMemo(
    () => userPreferences[UserPreferenceKeys.onboardingDone],
    [userPreferences]
  );

  useEffect(() => {
    void checkUnreadMessages();
    registerWebsocketObserversForNotification();
    void loadUserPreferences();
    // remove zendesk widget
    SignupCubit.removeSupportChat();
  }, []);

  useEffect(() => {
    const ionContent = document.querySelector("ion-content");
    const scrollable = ionContent?.shadowRoot?.querySelector(".inner-scroll");
    scrollable?.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // clear onboarding data and redirect to '/app/messenger' from '/app'
  useEffect(() => {
    const target = createAppPath(Path.home);

    if (
      (location.pathname as Path) === Path.root ||
      location.pathname === `/${Path.app}`
    ) {
      const targetWithQuery = preserveQueryParams(target, location.search);
      navigate(targetWithQuery);
    }
  }, [onboardingDone]);

  return (
    <>
      <AppQueryPopupsRedirects />
      <AnnouncementCardDialog />
      <AppPageProvider>
        <CustomSuspense>
          <Async.CustomerIo />
        </CustomSuspense>
        <Prompts />
        <Routes>
          {/* redirect old "/messages" to new "/chat" */}
          <Route
            path={Path.messages}
            element={<RedirectWithQueryParams to={createAppPath(Path.chat)} />}
          />

          <Route
            path={`${Path.chat}/*`}
            element={
              <AnimatedPage data-testid="messages-page" flex disableSafeAreaTop>
                {isConversationsEnabled ? (
                  <LeapChatRouter />
                ) : (
                  <LegacyChatView />
                )}
              </AnimatedPage>
            }
          />

          <Route
            path={Path.home}
            element={
              <AnimatedPage data-testid="home-page" spaceY="both">
                <HomePage />
              </AnimatedPage>
            }
          />

          <Route
            path={`${Path.signupQuestionnaire}/:id`}
            element={
              <AnimatedPage
                data-testid="in-app-questionnaire-page"
                spaceY="both"
              >
                <InAppQuestionnaire />
              </AnimatedPage>
            }
          />

          <Route
            path={Path.myPlan}
            element={
              <AnimatedPage
                data-testid="plan-overview-page"
                spaceY="bottom"
                disableSafeAreaTop
              >
                <ProgramOverview />
                <PlanOverview />
              </AnimatedPage>
            }
          />

          <Route
            path={`${Path.myPlan}/*`}
            element={
              <AnimatedPage data-testid="plan-sub-page" spaceY="bottom">
                <PlanRouter />
              </AnimatedPage>
            }
          />

          <Route
            path={Path.program}
            element={
              <AnimatedPage data-testid="program-page" spaceY="bottom">
                <ProgramDetails />
              </AnimatedPage>
            }
          />

          <Route
            path={`${Path.mealInsights}/:id`}
            element={
              <AnimatedPage id="meal-insights" data-testid="meal-insights-page">
                <MealInsightOverview />
              </AnimatedPage>
            }
          />

          <Route
            path={`${Path.profile}/*`}
            element={
              <AnimatedPage data-testid="profile-router-page" spaceY="both">
                <ProfileRouter />
              </AnimatedPage>
            }
          />

          <Route
            path={Path.profile}
            element={
              <AnimatedPage data-testid="profile-page" spaceY="both">
                <ProfileOverview />
              </AnimatedPage>
            }
          />

          <Route
            path={`${Path.labResults}/*`}
            element={
              <AnimatedPage
                data-testid="lab-results-page"
                spaceY="bottom"
                disableSafeAreaTop
              >
                <LabResultsDataSubPageRouter />
              </AnimatedPage>
            }
          />

          <Route
            path="*"
            element={<RedirectWithQueryParams to={Path.home} />}
          />
        </Routes>
      </AppPageProvider>
      <AppStatusBar />
      <ReadMore
        hidden
        name="labinstructions"
        slug={BLOOD_SPOT_SKU}
        fetchFromCms={false}
      />
    </>
  );
};

export default AppRouter;
